import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShoppingBag, Zap, Shield, Sparkles } from 'lucide-react';
import { PRODUCTS } from '../data/productData';

const ProductsPage = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" }
        }
    };

    return (
        <div className="pt-24 min-h-screen bg-white">
            {/* Page Hero */}
            <section className="relative py-24 bg-dark text-white overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 -left-1/4 w-1/2 h-full bg-primary/40 blur-[120px] rounded-full" />
                    <div className="absolute bottom-0 -right-1/4 w-1/2 h-full bg-secondary/40 blur-[120px] rounded-full" />
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center max-w-4xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm font-bold tracking-widest uppercase mb-8"
                        >
                            <Sparkles size={16} className="text-primary" /> Our Product Suite
                        </motion.div>
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-5xl md:text-7xl font-bold mb-8"
                        >
                            Scaling Businesses with <span className="text-primary">Next-Gen</span> Software
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-xl text-gray-400 leading-relaxed"
                        >
                            Explore our comprehensive suite of digital products designed to streamline operations,
                            enhance customer experience, and drive sustainable growth for modern enterprises.
                        </motion.p>
                    </div>
                </div>
            </section>

            {/* Products Grid */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12"
                    >
                        {PRODUCTS.map((product) => (
                            <motion.div
                                key={product.id}
                                variants={itemVariants}
                                whileHover={{ y: -10 }}
                                className="group bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-soft hover:shadow-2xl transition-all duration-300"
                            >
                                {/* Image / Cover */}
                                <div className={`relative h-64 bg-gradient-to-br ${product.color} overflow-hidden`}>
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                    <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm p-3 rounded-2xl shadow-lg">
                                        <span className="text-3xl">{product.icon}</span>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-8 space-y-4">
                                    <div>
                                        <h3 className="text-2xl font-bold text-dark mb-1">{product.name}</h3>
                                        <p className="text-primary font-bold text-sm tracking-wide uppercase">{product.tagline}</p>
                                    </div>
                                    <p className="text-gray-600 line-clamp-3 leading-relaxed">
                                        {product.description}
                                    </p>

                                    <div className="pt-4 flex items-center justify-between">
                                        <button
                                            onClick={() => navigate(`/products/${product.slug}`)}
                                            className="px-6 py-3 bg-primary text-white rounded-xl font-bold flex items-center gap-2 hover:bg-secondary transition-all"
                                        >
                                            View Details <ArrowRight size={18} />
                                        </button>
                                        <div className="flex -space-x-2">
                                            <div className="w-8 h-8 rounded-full bg-light border-2 border-white flex items-center justify-center text-primary"><Zap size={14} /></div>
                                            <div className="w-8 h-8 rounded-full bg-light border-2 border-white flex items-center justify-center text-primary"><Shield size={14} /></div>
                                            <div className="w-8 h-8 rounded-full bg-light border-2 border-white flex items-center justify-center text-primary"><ShoppingBag size={14} /></div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Platform Trust Section */}
            <section className="py-24 bg-light overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="text-4xl font-bold text-dark mb-8 leading-tight">
                                Built for <span className="text-primary">Reliability</span> & Massive Scale
                            </h2>
                            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
                                Every product in our suite is developed using enterprise-grade architecture, ensuring
                                consistent performance even during peak loads. We prioritize security, scalability,
                                and user experience in every line of code.
                            </p>

                            <div className="grid grid-cols-2 gap-8">
                                <div>
                                    <p className="text-4xl font-bold text-primary mb-2">99.9%</p>
                                    <p className="font-bold text-dark uppercase tracking-widest text-xs">Platform Uptime</p>
                                </div>
                                <div>
                                    <p className="text-4xl font-bold text-secondary mb-2">50+</p>
                                    <p className="font-bold text-dark uppercase tracking-widest text-xs">Active Deployments</p>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            className="relative grid grid-cols-2 gap-4"
                        >
                            <div className="space-y-4 pt-12">
                                <div className="h-48 rounded-3xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                                    <Zap size={48} className="text-primary" />
                                </div>
                                <div className="h-64 rounded-3xl bg-secondary/10 border border-secondary/20 overflow-hidden">
                                    <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80" alt="Stats" className="w-full h-full object-cover opacity-60 grayscale hover:grayscale-0 transition-all" />
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className="h-64 rounded-3xl bg-secondary/10 border border-secondary/20 overflow-hidden">
                                    <img src="https://images.unsplash.com/photo-1460925895917-adf4e5542483?w=600&q=80" alt="Code" className="w-full h-full object-cover opacity-60 grayscale hover:grayscale-0 transition-all" />
                                </div>
                                <div className="h-48 rounded-3xl bg-dark border border-white/10 flex items-center justify-center">
                                    <Shield size={48} className="text-white" />
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center bg-primary rounded-[3rem] p-16 shadow-2xl relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
                    <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 relative z-10">Need a Custom Product Solution?</h2>
                    <p className="text-white/80 text-lg mb-12 max-w-2xl mx-auto relative z-10">
                        Our experts specialize in building tailored digital solutions that solve unique business challenges.
                        Let's discuss your vision today.
                    </p>
                    <button
                        onClick={() => navigate('/contact')}
                        className="px-12 py-5 bg-white text-primary rounded-2xl font-bold text-xl hover:bg-secondary hover:text-white transition-all shadow-xl relative z-10"
                    >
                        Schedule a Consultation
                    </button>
                </div>
            </section>
        </div>
    );
};

export default ProductsPage;
